/**
 * AgentDownload — página PÚBLICA de descarga del agente CMDB.
 *
 * Ruta: `/instalar-agente` (fuera del portón de autenticación, ver `App.jsx`).
 *
 * POR QUÉ EXISTE
 * --------------
 * Quien va a instalar el agente en un servidor todavía no tiene cuenta en el
 * CMDB. Obligarle a iniciar sesión para bajar el MSI era un paso que no aportaba
 * nada y atascaba las instalaciones (y el alta de la cuenta la hace el
 * administrador de la empresa, no el técnico que está en el servidor).
 *
 * QUÉ ENSEÑA
 * ----------
 * Solo lo que un administrador haya publicado a propósito en
 * *Agente → Distribución*: MSI, EXE y ZIP, con versión, tamaño, SHA-256 y fecha,
 * más las instrucciones de instalación de cada formato.
 *
 * IMPORTANTE — nada de `api.js`
 * -----------------------------
 * El cliente de `services/api.js` intercepta los 401 y manda al SSO. Aquí no:
 * se usa `fetch` nativo contra `/api/public/agent/releases`, que no pide sesión
 * y nunca va a devolver 401. Mezclarlos provocaría un bucle de redirecciones a
 * `/api/auth/login` desde una página que, por definición, es anónima.
 */

import React, { useEffect, useState } from 'react';
import {
  ArrowDownTrayIcon,
  ArrowTopRightOnSquareIcon,
  CheckIcon,
  CircleStackIcon,
  ComputerDesktopIcon,
  DocumentDuplicateIcon,
  ServerStackIcon,
  ShieldCheckIcon,
} from '@heroicons/react/24/outline';
import '../styles/agent-download.css';

// Mismo criterio que `services/api.js`: cadena vacía ⇒ URLs relativas, que es
// lo que hay en Docker (nginx hace de proxy) y en producción.
const API_ROOT = (process.env.REACT_APP_API_URL ?? window.__ENV__?.API_URL ?? '').replace(/\/$/, '');

const ROLES = [
  { id: 'host', label: 'host', text: 'inventaría el equipo donde se instala.' },
  { id: 'network', label: 'network', text: 'solo ejecuta los descubrimientos de red configurados en CMDB.' },
];

function fmtSize(bytes) {
  if (!bytes) return '—';
  const mb = bytes / (1024 * 1024);
  return mb >= 1 ? `${mb.toFixed(1)} MB` : `${Math.max(1, Math.round(bytes / 1024))} KB`;
}

function fmtDate(value) {
  if (!value) return '—';
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? '—' : date.toLocaleString('es-ES');
}

/**
 * Descarga un texto como fichero, sin pasar por el servidor.
 *
 * Los scripts del generador llevan la API key: se construyen y se descargan
 * **en el navegador**. Nada de esto viaja al backend ni se guarda.
 */
function descargarTexto(nombre, contenido) {
  const blob = new Blob([contenido], { type: 'text/plain;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const enlace = document.createElement('a');
  enlace.href = url;
  enlace.download = nombre;
  document.body.appendChild(enlace);
  enlace.click();
  document.body.removeChild(enlace);
  URL.revokeObjectURL(url);
}

/** Bloque de comandos con botón de copiar (sin dependencias). */
function CodeBlock({ code }) {
  const [copied, setCopied] = useState(false);

  const copiar = async () => {
    try {
      await navigator.clipboard.writeText(code);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1600);
    } catch {
      // Sin permiso de portapapeles (http, navegador viejo): se copia a mano.
      setCopied(false);
    }
  };

  return (
    <div className="ad-code-wrap">
      <button type="button" className="ad-copy" onClick={copiar}>
        {copied ? <CheckIcon style={{ width: 14, height: 14 }} /> : <DocumentDuplicateIcon style={{ width: 14, height: 14 }} />}
        {copied ? 'Copiado' : 'Copiar'}
      </button>
      <pre className="ad-code"><code>{code}</code></pre>
    </div>
  );
}

function DescargaCard({ titulo, subtitulo, info, destacada, descripcion }) {
  const disponible = !!info?.available;
  const href = disponible ? `${API_ROOT}${info.download_url}` : undefined;
  // «Recomendado» solo tiene sentido si ese formato está publicado: una tarjeta
  // vacía recomendando algo que no se puede descargar confunde.
  const resaltar = Boolean(destacada && disponible);

  return (
    <div className={`ad-card${resaltar ? ' ad-card--primary' : ''}`}>
      <div className="ad-card-head">
        <h3 className="ad-card-title">{titulo}</h3>
        {resaltar && <span className="ad-chip ad-chip--accent">Recomendado</span>}
      </div>
      <p className="ad-card-sub">{subtitulo}</p>

      {disponible ? (
        <>
          <div className="ad-meta">
            <span className="ad-chip ad-chip--on">v{info.version || '—'}</span>
            <span>{fmtSize(info.size)}</span>
            <span>{fmtDate(info.uploaded_at)}</span>
          </div>
          {info.filename && <div className="ad-filename" title={info.filename}>{info.filename}</div>}
          {info.notes && <p className="ad-note">{info.notes}</p>}
          {info.sha256 && (
            <div className="ad-hash">
              <span>SHA-256</span>
              <code>{info.sha256}</code>
            </div>
          )}
          <a className={`ad-btn${resaltar ? ' ad-btn--primary' : ''}`} href={href} download>
            <ArrowDownTrayIcon style={{ width: 16, height: 16 }} />
            Descargar {titulo.split(' ').pop()}
          </a>
        </>
      ) : (
        <div className="ad-empty">
          <p>Todavía no hay nada publicado en este formato.</p>
          <p className="ad-hint">Pídelo al administrador del CMDB (Agente → Distribución).</p>
        </div>
      )}

      <p className="ad-hint">{descripcion}</p>
    </div>
  );
}

export default function AgentDownload() {
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const [tab, setTab] = useState('msi');
  // Generador de scripts: todo local, la clave no se envía a ningún sitio.
  const [genKey, setGenKey] = useState('');
  const [genRole, setGenRole] = useState('host');
  const [genHost, setGenHost] = useState('SRV01');

  const origen = `${window.location.origin}`;
  const artifacts = data?.artifacts || {};
  const msi = artifacts.msi;
  const exe = artifacts.exe;
  const pkg = artifacts.package;

  const nombreMsi = msi?.filename || 'CMDBAgent-<versión>.msi';
  const nombreExe = exe?.filename || 'CMDBAgentSetup-<versión>.exe';
  const nombreZip = pkg?.filename || 'cmdb-agent-linux-<versión>.zip';

  useEffect(() => {
    document.title = 'Agente CMDB · Descarga e instalación';
  }, []);

  useEffect(() => {
    let cancelled = false;
    fetch(`${API_ROOT}/api/public/agent/releases`, { headers: { Accept: 'application/json' } })
      .then((res) => {
        if (!res.ok) throw new Error(`No disponible (${res.status})`);
        return res.json();
      })
      .then((payload) => { if (!cancelled) setData(payload); })
      .catch((err) => { if (!cancelled) setError(err.message || 'No se pudo consultar las versiones publicadas'); });

    return () => { cancelled = true; };
  }, []);

  const cmdMsi = [
    `msiexec /i "${nombreMsi}" /qn /norestart \``,
    `  CMDB_URL="${origen}" \``,
    '  CMDB_COMPANY_API_KEY="<api-key>" `',
    '  CMDB_AGENT_ROLE="host"',
  ].join('\n');

  const cmdMsiActualizar = `msiexec /i "${nombreMsi}" /qn /norestart`;

  const cmdExeSilencioso = `"${nombreExe}" /S /ROLE=host`;

  const cmdLinuxPreparar = [
    'sudo apt update && sudo apt install -y unzip python3 python3-venv',
    `cd /tmp && unzip ${nombreZip} && cd cmdb-agent`,
    'chmod +x install_linux.sh',
  ].join('\n');

  const cmdLinux = [
    `sudo ./install_linux.sh --url ${origen} \\`,
    '  --api-key "<api-key>" --role network --non-interactive',
  ].join('\n');

  const cmdLinuxComprobar = [
    'systemctl status cmdb-agent --no-pager',
    'systemctl list-timers cmdb-agent-update.timer',
    'tail -f /opt/cmdb-agent/cmdb-agent.log',
    '',
    '# probar un equipo suelto con las credenciales del perfil',
    'sudo -u cmdbagent /opt/cmdb-agent/.venv/bin/python \\',
    '  /opt/cmdb-agent/agent.py network --probe 10.0.0.10 --dry-run',
  ].join('\n');

  const cmdLinuxActualizar = [
    '# a mano, ahora mismo (descarga el ZIP publicado en la CMDB)',
    'sudo /opt/cmdb-agent/.venv/bin/python /opt/cmdb-agent/agent.py update',
    'sudo systemctl restart cmdb-agent',
    '',
    '# o reinstalando con el ZIP nuevo: conserva .env y la identidad',
    `sudo ./install_linux.sh --url ${origen} --role network --non-interactive`,
  ].join('\n');

  const cmdVerificar = [
    'Get-Service CMDBAgent',
    '',
    '# SHA-256 del fichero descargado (compáralo con el de esta página)',
    `Get-FileHash ".\\${nombreMsi}" -Algorithm SHA256`,
  ].join('\n');

  // ── MST (transform para directiva de grupo) ─────────────────────────────
  const cmdMstAplicar = [
    `msiexec /i "${nombreMsi}" /qn /norestart \``,
    '  TRANSFORMS="CMDBAgent.mst" `',
    '  /L*v "C:\\ProgramData\\CMDBAgentDeploy\\agent.log"',
  ].join('\n');

  // ── Instalación en un equipo remoto ─────────────────────────────────────
  const cmdRemoto = [
    "$pc   = 'SRV01'",
    '$cred = Get-Credential',
    '',
    '# 1) Copiar el MSI al equipo (recurso administrativo C$)',
    `Copy-Item ".\\${nombreMsi}" "\\\\$pc\\C$\\Windows\\Temp\\" -Credential $cred`,
    '',
    '# 2) Instalar en silencio en el equipo remoto',
    'Invoke-Command -ComputerName $pc -Credential $cred -ScriptBlock {',
    `  $msi = 'C:\\Windows\\Temp\\${nombreMsi}'`,
    "  $log = 'C:\\Windows\\Temp\\cmdb-agent.log'",
    `  $arg = "/i $msi /qn /norestart /L*v $log CMDB_URL=${origen} CMDB_COMPANY_API_KEY=<api-key> CMDB_AGENT_ROLE=host"`,
    '  $p = Start-Process msiexec.exe -ArgumentList $arg -Wait -PassThru',
    '  "ExitCode: $($p.ExitCode)"',
    '  Get-Service CMDBAgent',
    '}',
  ].join('\n');

  const cmdPsexec = `psexec.exe \\\\SRV01 -u DOMINIO\\admin -h -accepteula cmd /c msiexec /i "C:\\Windows\\Temp\\${nombreMsi}" /qn /norestart CMDB_URL="${origen}" CMDB_COMPANY_API_KEY="<api-key>" CMDB_AGENT_ROLE="host"`;

  const cmdWmi = [
    `$arg = "/i C:\\Windows\\Temp\\${nombreMsi} /qn /norestart CMDB_URL=${origen} CMDB_COMPANY_API_KEY=<api-key> CMDB_AGENT_ROLE=host"`,
    'Invoke-CimMethod -ComputerName SRV01 -Credential (Get-Credential) `',
    '  -ClassName Win32_Process -MethodName Create `',
    '  -Arguments @{ CommandLine = "cmd.exe /c msiexec $arg" }',
  ].join('\n');

  const cmdRemotoComprobar = [
    'Invoke-Command -ComputerName SRV01 -Credential $cred -ScriptBlock {',
    '  Get-Service CMDBAgent',
    '}',
    '',
    '# Log de la instalación',
    'Get-Content "\\\\SRV01\\C$\\Windows\\Temp\\cmdb-agent.log" -Tail 30',
  ].join('\n');

  const cmdRemotoActualizar = `msiexec /i "C:\\Windows\\Temp\\CMDBAgent-<nueva-versión>.msi" /qn /norestart`;

  const cmdRemotoDesinstalar = `msiexec /x "C:\\Windows\\Temp\\${nombreMsi}" /qn /norestart`;

  // ── Generador: ficheros listos con URL, clave y rol ─────────────────────
  const claveScript = genKey.trim() || '<api-key>';

  // .cmd para instalar en el propio equipo (consola como administrador).
  const scriptLocal = [
    '@echo off',
    'REM Instalacion silenciosa del agente CMDB. Ejecutar como administrador.',
    'REM OJO: este fichero contiene la API key; tratalo como una credencial.',
    'setlocal',
    `set "MSI=%~dp0${nombreMsi}"`,
    'set "LOG=%ProgramData%\\CMDBAgentDeploy\\agent.log"',
    'if not exist "%ProgramData%\\CMDBAgentDeploy" mkdir "%ProgramData%\\CMDBAgentDeploy"',
    'msiexec /i "%MSI%" /qn /norestart /L*v "%LOG%" ^',
    `  CMDB_URL="${origen}" ^`,
    `  CMDB_COMPANY_API_KEY="${claveScript}" ^`,
    `  CMDB_AGENT_ROLE="${genRole}"`,
    'echo Codigo de salida: %ERRORLEVEL%  (0 = correcto, 3010 = reiniciar)',
    'pause',
  ].join('\r\n');

  // .ps1 para instalar en uno o varios equipos por PowerShell Remoting.
  const equipos = genHost.split(',').map((h) => h.trim()).filter(Boolean);
  const equiposPorDefecto = equipos.length
    ? `@(${equipos.map((h) => `'${h}'`).join(', ')})`
    : "@('SRV01')";
  const scriptRemoto = [
    '<#',
    '  Instala el agente CMDB en silencio en uno o varios equipos.',
    '  Ejecutar en el equipo de administracion, con el MSI en la misma carpeta.',
    '  OJO: este fichero contiene la API key; tratalo como una credencial.',
    '  Ejemplo: .\\instalar-agente-remoto.ps1 -ComputerName SRV01,SRV02',
    '#>',
    'param(',
    `  [string[]]$ComputerName = ${equiposPorDefecto},`,
    `  [string]$Msi = (Join-Path $PSScriptRoot '${nombreMsi}'),`,
    '  [string]$Usuario',
    ')',
    "$ErrorActionPreference = 'Stop'",
    "$cred = Get-Credential -UserName $Usuario -Message 'Credenciales de administrador en los equipos destino'",
    '$nombre = Split-Path $Msi -Leaf',
    `$arg = '/i C:\\Windows\\Temp\\' + $nombre + ' /qn /norestart /L*v C:\\Windows\\Temp\\cmdb-agent.log CMDB_URL=${origen} CMDB_COMPANY_API_KEY=${claveScript} CMDB_AGENT_ROLE=${genRole}'`,
    '',
    'foreach ($pc in $ComputerName) {',
    '  Write-Host "=== $pc ==="',
    "  Copy-Item $Msi ('\\\\' + $pc + '\\C$\\Windows\\Temp\\') -Credential $cred -Force",
    '  $r = Invoke-Command -ComputerName $pc -Credential $cred -ScriptBlock {',
    '    param($argumentos)',
    '    $p = Start-Process msiexec.exe -ArgumentList $argumentos -Wait -PassThru',
    '    [pscustomobject]@{',
    '      ExitCode = $p.ExitCode',
    '      Servicio = (Get-Service CMDBAgent -ErrorAction SilentlyContinue).Status',
    '    }',
    '  } -ArgumentList $arg',
    '  $r | Format-List',
    '}',
  ].join('\r\n');

  // .ps1 que crea el MST en el equipo de administracion (API COM de Windows Installer).
  const scriptMst = [
    '<#',
    '  Crea el MST (transform) del agente CMDB con URL, API key y rol.',
    '  Se ejecuta en el equipo de administracion (Windows), junto al MSI.',
    '  OJO: el MST resultante contiene la API key; tratalo como una credencial.',
    `  Ejemplo: .\\crear-mst.ps1 -Msi .\\${nombreMsi}`,
    '#>',
    'param(',
    `  [string]$Msi = (Join-Path $PSScriptRoot '${nombreMsi}'),`,
    `  [string]$Url = '${origen}',`,
    `  [string]$ApiKey = '${claveScript}',`,
    `  [ValidateSet('host', 'network')][string]$Role = '${genRole}',`,
    "  [string]$Salida = 'CMDBAgent.mst'",
    ')',
    "$ErrorActionPreference = 'Stop'",
    '$Msi = (Resolve-Path $Msi).Path',
    '$Salida = [IO.Path]::Combine((Get-Location).Path, $Salida)',
    '',
    '# 1 = msiOpenDatabaseModeTransact: los cambios van a memoria y el MSI no se modifica',
    '$installer = New-Object -ComObject WindowsInstaller.Installer',
    '$db = $installer.OpenDatabase($Msi, 1)',
    '',
    '$valores = [ordered]@{',
    '  CMDB_URL             = $Url',
    '  CMDB_COMPANY_API_KEY = $ApiKey',
    '  CMDB_AGENT_ROLE      = $Role',
    '}',
    'foreach ($clave in $valores.Keys) {',
    '  $valor = $valores[$clave] -replace "\'", "\'\'"',
    '  $sql = "INSERT INTO ``Property`` (``Property``,``Value``) VALUES (\'$clave\',\'$valor\')"',
    '  $vista = $db.OpenView($sql)',
    '  $vista.Execute()',
    '  $vista.Close()',
    '}',
    '',
    'try {',
    '  # Referencia: el mismo MSI abierto en solo lectura (0 = ReadOnly)',
    '  $ref = $installer.OpenDatabase($Msi, 0)',
    '  $db.GenerateTransform($ref, $Salida)',
    '  $db.CreateTransformSummaryInfo($ref, $Salida, 0, 0)',
    '} catch {',
    '  throw "No se pudo generar el MST: $($_.Exception.Message). Como alternativa, crealo con InstEd u Orca (instrucciones mas arriba)."',
    '}',
    '# Sin Commit(): el MSI original se queda como estaba',
    'foreach ($objeto in @($db, $ref, $installer)) {',
    '  if ($null -ne $objeto) { [void][Runtime.InteropServices.Marshal]::ReleaseComObject($objeto) }',
    '}',
    'Write-Host "MST creado: $Salida"',
  ].join('\r\n');

  return (
    <div className="ad-page">
      <div className="ad-glow" aria-hidden="true" />

      <div className="ad-wrap">
        <header className="ad-top">
          <div className="ad-brand">
            <span className="ad-brand-mark"><CircleStackIcon style={{ width: 20, height: 20 }} /></span>
            <span className="ad-brand-name">Adder <em>CMDB</em></span>
          </div>
          <a className="ad-link" href="/login">
            Acceder <ArrowTopRightOnSquareIcon style={{ width: 14, height: 14 }} />
          </a>
        </header>

        <section className="ad-hero">
          <h1 className="ad-h1">Agente CMDB</h1>
          <p className="ad-sub">
            Descarga e instalación del agente de inventario. <strong>No hace falta iniciar sesión</strong>:
            los instaladores están publicados por el administrador de tu CMDB.
          </p>
          <div className="ad-hero-chips">
            <span className="ad-chip"><ServerStackIcon style={{ width: 14, height: 14 }} /> Windows 10/11 y Server 2016+</span>
            <span className="ad-chip"><ComputerDesktopIcon style={{ width: 14, height: 14 }} /> Linux con systemd</span>
            <span className="ad-chip"><ShieldCheckIcon style={{ width: 14, height: 14 }} /> Sólo HTTPS hacia el CMDB</span>
          </div>
          {error && <div className="ad-alert">{error}. Recarga la página o pide el instalador a tu administrador.</div>}
        </section>

        <section className="ad-section" id="descargas">
          <h2 className="ad-h2">Descargas</h2>
          <div className="ad-grid">
            <DescargaCard
              titulo="Instalador MSI"
              subtitulo="WiX · instalación silenciosa, GPO o Intune"
              info={msi}
              destacada
              descripcion="Sin asistente: URL y clave se pasan como propiedades. Es el formato bueno para desplegar en muchos equipos."
            />
            <DescargaCard
              titulo="Instalador EXE"
              subtitulo="NSIS · instalación interactiva"
              info={exe}
              descripcion="Abre el panel del agente al terminar para escribir la URL y la clave a mano. También admite modo silencioso."
            />
            <DescargaCard
              titulo="Paquete ZIP"
              subtitulo="Linux y actualización manual"
              info={pkg}
              descripcion="Código del agente con install_linux.sh. Es el mismo paquete que usa el auto-update."
            />
          </div>
        </section>

        <section className="ad-section" id="antes">
          <h2 className="ad-h2">Antes de instalar</h2>
          <div className="ad-grid ad-grid--3">
            <div className="ad-step">
              <span className="ad-step-num">1</span>
              <h3>Pide una API key</h3>
              <p>
                La genera un administrador en <strong>CMDB → Agente → Credenciales → Nueva clave de empresa</strong>.
                Es lo que autoriza al agente a reportar.
              </p>
            </div>
            <div className="ad-step">
              <span className="ad-step-num">2</span>
              <h3>Ten la URL del servidor</h3>
              <p>La base del CMDB, sin <code>/api</code>:</p>
              <code className="ad-inline-code">{origen}</code>
            </div>
            <div className="ad-step">
              <span className="ad-step-num">3</span>
              <h3>Elige el rol</h3>
              <p>
                {ROLES.map((rol) => (
                  <span key={rol.id} className="ad-role">
                    <code>{rol.label}</code> {rol.text}
                  </span>
                ))}
              </p>
            </div>
          </div>
        </section>

        <section className="ad-section" id="instalacion">
          <h2 className="ad-h2">Instalación</h2>

          <div className="ad-tabs" role="tablist">
            <button
              type="button"
              role="tab"
              aria-selected={tab === 'msi'}
              className={`ad-tab${tab === 'msi' ? ' is-active' : ''}`}
              onClick={() => setTab('msi')}
            >
              Windows · MSI
            </button>
            <button
              type="button"
              role="tab"
              aria-selected={tab === 'exe'}
              className={`ad-tab${tab === 'exe' ? ' is-active' : ''}`}
              onClick={() => setTab('exe')}
            >
              Windows · EXE
            </button>
            <button
              type="button"
              role="tab"
              aria-selected={tab === 'linux'}
              className={`ad-tab${tab === 'linux' ? ' is-active' : ''}`}
              onClick={() => setTab('linux')}
            >
              Linux
            </button>
          </div>

          {tab === 'msi' && (
            <div className="ad-panel">
              <p className="ad-p">
                Instalación desatendida desde una consola <strong>como administrador</strong>. El MSI crea el
                fichero <code>.env</code>, instala el servicio <code>CMDBAgent</code> y lo arranca.
              </p>
              <CodeBlock code={cmdMsi} />
              <p className="ad-p">
                Para cambiar el rol, sustituye <code>host</code> por <code>network</code>. Si prefieres que el rol
                sea otro, omite <code>CMDB_AGENT_ROLE</code>: en una instalación nueva se asume <code>host</code>.
              </p>
              <h3 className="ad-h3">Actualizar un equipo que ya tiene el agente</h3>
              <p className="ad-p">
                Con el MSI nuevo, sin propiedades: se conservan la URL, la clave y el rol guardados en <code>.env</code>.
              </p>
              <CodeBlock code={cmdMsiActualizar} />
              <h3 className="ad-h3">Despliegue por directiva (GPO / Intune)</h3>
              <p className="ad-p">
                Publica el MSI en una ruta de red y añade <code>CMDB_URL</code>, <code>CMDB_COMPANY_API_KEY</code> y{' '}
                <code>CMDB_AGENT_ROLE</code> en un MST (o en el script de instalación). Aplica la política a la OU de
                <strong> equipos</strong>, no de usuarios.
              </p>
            </div>
          )}

          {tab === 'exe' && (
            <div className="ad-panel">
              <p className="ad-p">
                <strong>Interactivo:</strong> ejecuta el EXE como administrador. Al terminar se abre el panel del
                agente: escribe la URL, la API key y el rol, pulsa <em>Validar</em> y luego <em>Guardar</em>. El
                servicio arranca solo.
              </p>
              <p className="ad-p"><strong>Silencioso</strong> (sin API key; se configura después en el panel):</p>
              <CodeBlock code={cmdExeSilencioso} />
              <p className="ad-p">
                El EXE no acepta la clave por línea de comandos. Si necesitas clave en el desatendido, usa el MSI.
              </p>
            </div>
          )}

          {tab === 'linux' && (
            <div className="ad-panel">
              <p className="ad-p">
                Un servidor Linux pequeño en la VLAN de gestión (1 vCPU, 1 GB de RAM, 8 GB de disco). Necesita
                <code> python3</code> con <code>venv</code>, <code>unzip</code> y <code>systemd</code>; el instalador
                crea el usuario <code>cmdbagent</code> y el servicio <code>cmdb-agent</code>.
                {' '}<strong>No sirve en pfSense ni en otros FreeBSD</strong> (no hay systemd) ni en contenedores sin
                systemd: para eso, usa la imagen Docker del propio ZIP.
              </p>

              <h3 className="ad-h3">1 · Preparar la máquina</h3>
              <CodeBlock code={cmdLinuxPreparar} />

              <h3 className="ad-h3">2 · Instalar</h3>
              <CodeBlock code={cmdLinux} />
              <p className="ad-p">
                <code>--role network</code> instala un <strong>colector</strong>: no inventaría la propia máquina y
                solo ejecuta los ámbitos de descubrimiento. Para inventariar este servidor, cambia a{' '}
                <code>--role host</code>. Otras opciones: <code>--slug</code> + <code>--token</code> (bootstrap),
                {' '}<code>--install-dir</code>, <code>--force-reconfigure</code>, <code>--no-start</code> y
                {' '}<code>--no-auto-update</code>.
              </p>

              <h3 className="ad-h3">3 · Comprobar</h3>
              <CodeBlock code={cmdLinuxComprobar} />
              <p className="ad-p">
                El servicio escribe <code>/opt/cmdb-agent/cmdb-agent.log</code> y el barrido deja una línea por IP
                con el motivo cuando descarta un equipo. El colector necesita salida <strong>HTTPS</strong> al CMDB y
                {' '}<strong>UDP/161</strong> hacia los equipos que tiene que descubrir.
              </p>

              <h3 className="ad-h3">4 · Actualizaciones</h3>
              <p className="ad-p">
                El instalador deja activo el timer <code>cmdb-agent-update.timer</code>: una vez al día descarga el
                ZIP publicado en la CMDB, sustituye el código, refresca las dependencias del <code>venv</code> y
                reinicia el servicio. Los <code>.exe</code> no se usan en Linux.
              </p>
              <CodeBlock code={cmdLinuxActualizar} />
            </div>
          )}
        </section>

        <section className="ad-section" id="mst">
          <h2 className="ad-h2">Despliegue masivo: crear el MST</h2>
          <p className="ad-p">
            Un <strong>MST</strong> (transform) es un fichero que se aplica <em>encima</em> del MSI y
            rellena las propiedades sin tocar el instalador original. Solo es necesario si despliegas con{' '}
            <strong>Directiva de grupo → Instalación de software</strong> en modo <em>asignado</em>, porque
            ahí no se pueden pasar propiedades por la línea de comandos. Si despliegas con un script,
            PsExec, SCCM o Intune, pasa las propiedades directamente y <strong>no necesitas MST</strong>.
          </p>

          <h3 className="ad-h3">Programas que pueden generarlo</h3>
          <ul className="ad-list">
            <li><strong>InstEd</strong> — gratis y el más cómodo. <strong>Recomendado</strong>.</li>
            <li><strong>Orca</strong> — gratis; viene con el Windows SDK de Microsoft.</li>
            <li><strong>SuperOrca</strong> — gratis; alternativa a Orca.</li>
            <li><strong>msitran.exe</strong> y <strong>WiX Toolset</strong> — por línea de comandos, para generar o aplicar el MST de forma automatizada.</li>
          </ul>
          <p className="ad-hint">
            Se instalan en el equipo de administración, no en los servidores. El MSI no se modifica en
            ningún caso: el MST es un fichero aparte que se aplica al instalar.
          </p>

          <h3 className="ad-h3">Paso a paso con InstEd (recomendado)</h3>
          <ol className="ad-list ad-list--num">
            <li>Descarga InstEd de <span className="ad-inline-code">instedit.com</span> e instálalo en tu equipo.</li>
            <li>Abre el MSI: <em>File → Open</em> y elige <span className="ad-inline-code">{nombreMsi}</span>.</li>
            <li>Menú <strong>Transform → New Transform</strong>.</li>
            <li>Abre la tabla <strong>Property</strong>, haz clic derecho y añade estas tres filas:</li>
          </ol>

          <table className="ad-table">
            <thead>
              <tr><th>Property</th><th>Value</th><th>Cuándo</th></tr>
            </thead>
            <tbody>
              <tr><td>CMDB_URL</td><td>{origen}</td><td>Siempre</td></tr>
              <tr><td>CMDB_COMPANY_API_KEY</td><td>&lt;api-key&gt;</td><td>Siempre (instalación nueva)</td></tr>
              <tr><td>CMDB_AGENT_ROLE</td><td>host &nbsp;o&nbsp; network</td><td>Opcional (por defecto host)</td></tr>
            </tbody>
          </table>

          <ol className="ad-list ad-list--num" start={5}>
            <li><strong>Transform → Save Transform As…</strong> y guarda <span className="ad-inline-code">CMDBAgent.mst</span>.</li>
            <li>Cierra InstEd y responde <strong>No</strong> si pregunta si quieres guardar el MSI.</li>
          </ol>

          <details className="ad-details">
            <summary>Paso a paso con Orca</summary>
            <ol className="ad-list ad-list--num">
              <li>Instala Orca: viene en el <strong>Windows SDK</strong>, en el apartado <em>MSI Tools</em>.</li>
              <li>Abre el MSI con Orca.</li>
              <li>Menú <strong>Transform → New Transform</strong>.</li>
              <li>En la tabla <strong>Property</strong>, clic derecho → <strong>Add Row</strong> y añade las tres filas de la tabla de arriba.</li>
              <li><strong>Transform → Generate Transform…</strong> y guarda el <span className="ad-inline-code">.mst</span>.</li>
              <li>Cierra Orca sin guardar el MSI.</li>
            </ol>
          </details>

          <details className="ad-details">
            <summary>Por línea de comandos (msitran / WiX)</summary>
            <p className="ad-p">
              Con el Windows SDK instalado, en una consola de administrador:
            </p>
            <CodeBlock
              code={[
                '# Generar un MST comparando el MSI original con uno ya modificado (Orca/InstEd)',
                `msitran -g "${nombreMsi}" "CMDBAgent-modificado.msi" "CMDBAgent.mst"`,
                '',
                '# Aplicar y comprobar un MST sobre una copia del MSI',
                `msitran -a "CMDBAgent.mst" "${nombreMsi}"`,
                '',
                '# WiX Toolset: aplicar el transform y generar el MSI resultante',
                `torch.exe -t "CMDBAgent.mst" "${nombreMsi}" "CMDBAgent-con-propiedades.msi"`,
              ].join('\n')}
            />
            <p className="ad-hint">
              Para crear el «MSI modificado» hace falta un editor (Orca o InstEd): la API de Windows
              Installer no escribe transforms directamente desde un script.
            </p>
          </details>

          <h3 className="ad-h3">Aplicar el MST</h3>
          <p className="ad-p">Instalación manual o por script:</p>
          <CodeBlock code={cmdMstAplicar} />

          <p className="ad-p"><strong>Por directiva de grupo:</strong></p>
          <ol className="ad-list ad-list--num">
            <li>Copia el MSI y el MST a una ruta UNC que los equipos puedan leer, por ejemplo <span className="ad-inline-code">\\dominio\netlogon\CMDB\</span>.</li>
            <li>GPMC → <em>Computer Configuration → Policies → Software Settings → Software installation</em> → <strong>New → Package</strong> → elige el MSI por la ruta UNC y modo <strong>Assigned</strong>.</li>
            <li>Abre las propiedades del paquete → pestaña <strong>Modifications</strong> → <strong>Add</strong> → elige el <span className="ad-inline-code">.mst</span>.</li>
            <li>Aplica la GPO a la OU de <strong>equipos</strong> (no de usuarios). En el equipo: <span className="ad-inline-code">gpupdate /force</span> y reinicia.</li>
            <li>Comprueba en el equipo: <span className="ad-inline-code">Get-Service CMDBAgent</span>.</li>
          </ol>

          <p className="ad-p">
            <strong>Intune o SCCM:</strong> sube el MSI y el MST juntos y usa como comando de instalación
            el de arriba con <span className="ad-inline-code">TRANSFORMS="CMDBAgent.mst"</span>. También
            puedes pasar las propiedades directamente y prescindir del MST.
          </p>

          <div className="ad-warn">
            <strong>El MST lleva la API key en claro.</strong> Guárdalo en una ruta protegida (solo lectura
            para administradores), no lo subas a un repositorio y trátalo como una credencial: si se filtra,
            revoca la clave en <span className="ad-inline-code">CMDB → Agente → Credenciales</span>.
          </div>
        </section>

        <section className="ad-section" id="remoto">
          <h2 className="ad-h2">Instalar en un equipo remoto</h2>
          <p className="ad-p">
            Sí. El MSI se puede instalar <strong>a mano</strong> en el propio equipo (consola de
            administrador) o <strong>en silencio y en remoto</strong> desde tu equipo, sin entrar por
            escritorio remoto. El MSI no tiene asistente: para instalarlo a mano hay que ejecutar el
            comando; si quieres interfaz gráfica, usa el EXE.
          </p>

          <h3 className="ad-h3">Antes de empezar</h3>
          <ul className="ad-list">
            <li>Una cuenta con <strong>permisos de administrador</strong> en el equipo destino.</li>
            <li>El equipo destino tiene que poder abrir el CMDB por <strong>HTTPS</strong>.</li>
            <li><strong>PowerShell Remoting:</strong> WinRM habilitado (<span className="ad-inline-code">Enable-PSRemoting -Force</span> en el destino) y puerto 5985 (HTTP) o 5986 (HTTPS) abierto.</li>
            <li><strong>PsExec o WMI:</strong> recurso administrativo <span className="ad-inline-code">C$</span> accesible (puerto 445) y RPC.</li>
          </ul>

          <h3 className="ad-h3">Opción 1 — PowerShell Remoting (recomendada)</h3>
          <p className="ad-p">Se ejecuta en <strong>tu</strong> equipo, en una consola de PowerShell como administrador:</p>
          <CodeBlock code={cmdRemoto} />
          <p className="ad-hint">
            Con MST, copia también el <span className="ad-inline-code">.mst</span> a la misma carpeta y
            añade <span className="ad-inline-code">TRANSFORMS=&quot;C:\Windows\Temp\CMDBAgent.mst&quot;</span> al comando.
          </p>

          <h3 className="ad-h3">Opción 2 — PsExec</h3>
          <CodeBlock code={cmdPsexec} />
          <p className="ad-hint">
            Copia primero el MSI al equipo: <span className="ad-inline-code">Copy-Item .\{nombreMsi} \\SRV01\C$\Windows\Temp\</span>.
            Evita instalar directamente desde una ruta UNC: PsExec ejecuta como SYSTEM y la cuenta de
            equipo no siempre tiene permiso en el recurso compartido.
          </p>

          <h3 className="ad-h3">Opción 3 — WMI / CIM (sin WinRM)</h3>
          <CodeBlock code={cmdWmi} />
          <p className="ad-hint">
            Requiere SMB (445) y RPC accesibles. El proceso se lanza con las credenciales que pasas a
            <span className="ad-inline-code">Invoke-CimMethod</span>.
          </p>

          <h3 className="ad-h3">Opción 4 — GPO de arranque, SCCM o Intune</h3>
          <ul className="ad-list">
            <li><strong>GPO de arranque:</strong> un <span className="ad-inline-code">.cmd</span> en <em>Computer Configuration → Windows Settings → Scripts → Startup</em> con el comando del MSI. Activa <em>Always wait for the network at computer startup</em>.</li>
            <li><strong>SCCM / Intune:</strong> despliega el MSI como aplicación con el comando y las propiedades (o con el MST).</li>
          </ul>

          <h3 className="ad-h3">Comprobar el resultado</h3>
          <CodeBlock code={cmdRemotoComprobar} />
          <p className="ad-p">
            En el log, un cierre correcto termina con <em>Installation success or error status: 0</em>.
            Códigos habituales: <strong>0</strong> correcto, <strong>3010</strong> correcto pero pide
            reinicio, <strong>1603</strong> error grave (mira el log), <strong>1619</strong> no se pudo
            abrir el paquete (permisos o ruta UNC).
          </p>

          <h3 className="ad-h3">Actualizar y desinstalar en remoto</h3>
          <p className="ad-p">Actualizar: mismo comando con el MSI nuevo, <strong>sin propiedades</strong> (conserva URL, clave y rol del <span className="ad-inline-code">.env</span>):</p>
          <CodeBlock code={cmdRemotoActualizar} />
          <p className="ad-p">Desinstalar:</p>
          <CodeBlock code={cmdRemotoDesinstalar} />
        </section>

        <section className="ad-section" id="generador">
          <h2 className="ad-h2">Generador: scripts y MST con tus datos</h2>
          <p className="ad-p">
            Rellena la API key y el rol y descarga el fichero que necesites. Se genera{' '}
            <strong>en tu navegador</strong>: la clave no se envía al servidor ni se guarda aquí.
            El fichero descargado sí la contiene, así que trátalo como una credencial.
          </p>

          <div className="ad-gen">
            <label className="ad-field">
              API key
              <input
                type="password"
                value={genKey}
                onChange={(e) => setGenKey(e.target.value)}
                placeholder="<api-key> (opcional)"
                autoComplete="off"
                spellCheck="false"
              />
              <span className="ad-hint">Si la dejas vacía, el script sale con el marcador <code>&lt;api-key&gt;</code>.</span>
            </label>

            <label className="ad-field">
              Rol
              <select value={genRole} onChange={(e) => setGenRole(e.target.value)}>
                <option value="host">host — inventaría el propio equipo</option>
                <option value="network">network — solo descubrimiento de red</option>
              </select>
            </label>

            <label className="ad-field">
              Equipo(s) remoto(s)
              <input
                type="text"
                value={genHost}
                onChange={(e) => setGenHost(e.target.value)}
                placeholder="SRV01,SRV02"
                spellCheck="false"
              />
              <span className="ad-hint">Se usa como valor por defecto de <code>-ComputerName</code> en el script remoto.</span>
            </label>
          </div>

          <div className="ad-gen-actions">
            <button
              type="button"
              className="ad-btn ad-btn--primary"
              onClick={() => descargarTexto(`instalar-agente-${genRole}.cmd`, scriptLocal)}
            >
              <ArrowDownTrayIcon style={{ width: 16, height: 16 }} />
              Descargar .cmd (local)
            </button>
            <button
              type="button"
              className="ad-btn"
              onClick={() => descargarTexto('instalar-agente-remoto.ps1', scriptRemoto)}
            >
              <ArrowDownTrayIcon style={{ width: 16, height: 16 }} />
              Descargar .ps1 (remoto)
            </button>
            <button
              type="button"
              className="ad-btn"
              onClick={() => descargarTexto('crear-mst.ps1', scriptMst)}
            >
              <ArrowDownTrayIcon style={{ width: 16, height: 16 }} />
              Descargar .ps1 que crea el MST
            </button>
          </div>

          <ul className="ad-list">
            <li><strong>.cmd</strong> — instalación silenciosa en el equipo donde lo ejecutes. Debe estar en la misma carpeta que el MSI.</li>
            <li><strong>.ps1 remoto</strong> — instala en los equipos que le pases (<code>-ComputerName</code>) por PowerShell Remoting.</li>
            <li><strong>.ps1 que crea el MST</strong> — ejecútalo donde tengas el MSI; genera <code>CMDBAgent.mst</code> sin modificar el instalador. Después usa el MST como se explica arriba.</li>
          </ul>

          {!msi?.available && (
            <p className="ad-hint">
              Todavía no hay ningún MSI publicado: los scripts salen con el nombre de ejemplo{' '}
              <span className="ad-inline-code">{nombreMsi}</span>. Cámbialo por el del MSI que vayas a usar.
            </p>
          )}

          <div className="ad-warn">
            <strong>Los ficheros generados llevan la API key en claro.</strong> Guárdalos en una ruta
            protegida, no los subas a un repositorio y bórralos cuando termines. Si se filtran, revoca la
            clave en <span className="ad-inline-code">CMDB → Agente → Credenciales</span>.
          </div>
        </section>

        <section className="ad-section" id="comprobar">
          <h2 className="ad-h2">Comprobar que ha funcionado</h2>
          <p className="ad-p">
            El servicio debe quedar en <code>Running</code>. En el CMDB, el equipo aparece en el inventario con su
            versión de agente en cuanto manda el primer inventario.
          </p>
          <CodeBlock code={cmdVerificar} />
          <p className="ad-p">
            Si el servicio no arranca, revisa que el servidor llegue al CMDB por HTTPS y que la API key siga activa
            (<code>Agente → Credenciales</code>). Los registros del agente están en la carpeta de instalación
            (<code>C:\Program Files\CMDB Agent</code>).
          </p>
        </section>

        <footer className="ad-foot">
          <span>Adder CMDB · Agente de inventario</span>
          <a className="ad-link" href="/login">Acceder a la consola</a>
        </footer>
      </div>
    </div>
  );
}
